import {
  EVIDENCE_POWER_USER_BANNER_COUNT_THRESHOLD,
  evidencePowerUserBannerDismissedAtom,
  evidencePowerUserDevForceEnabledAtom,
  evidencePowerUserDevForceShowBannerAtom,
} from '@/atoms/evidenceTrialQueryCountAtom';
import { evidencePowerUserUpgradeModalOpenAtom } from '@/atoms/paywallAtoms';
import { IS_EVIDENCE_POWER_USER_UPGRADE_MODAL_ENABLED } from '@/features/flags/legacy-flags';
import { GET_USER, type QueryUserResponse } from '@/hooks/query/useQueryUser';
import { useUserActivityActionCount } from '@/hooks/useUserActivityActionCount';
import { Button } from '@heidi/design-system/button';
import { Switch } from '@heidi/design-system/switch';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { UserActivityAction } from '@heidi/schema/ml-scribe';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';

interface EvidencePowerUserDevSectionProps {
  /** Called after the user triggers an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}

/**
 * Dev-tools sidebar section for the Evidence power-user experience.
 * Exposes controls for the trial query counter, upgrade modal, and chat banner
 * so engineers + PMs can iterate without needing to run real Evidence queries.
 */
export const EvidencePowerUserDevSection = ({
  onRequestClose,
}: EvidencePowerUserDevSectionProps) => {
  const queryClient = useQueryClient();
  const { data: evidenceChatQueryCount = 0 } = useUserActivityActionCount(
    UserActivityAction.evidence_chat_query
  );
  const setEvidencePowerUserModalOpen = useSetAtom(evidencePowerUserUpgradeModalOpenAtom);
  const [bannerDismissed, setBannerDismissed] = useAtom(
    evidencePowerUserBannerDismissedAtom
  );
  const [devForceEnabled, setDevForceEnabled] = useAtom(
    evidencePowerUserDevForceEnabledAtom
  );
  const [devForceShowBanner, setDevForceShowBanner] = useAtom(
    evidencePowerUserDevForceShowBannerAtom
  );
  const flagEnabled = IS_EVIDENCE_POWER_USER_UPGRADE_MODAL_ENABLED();
  const effectivelyEnabled = flagEnabled || devForceEnabled;

  const patchEvidenceChatQueryCount = useCallback(
    (value: number | ((prev: number) => number)) => {
      queryClient.setQueryData<QueryUserResponse>([GET_USER], (old) => {
        if (!old) return old;
        const prev = old.user_activity?.[UserActivityAction.evidence_chat_query] ?? 0;
        const next = typeof value === 'function' ? value(prev) : value;
        return {
          ...old,
          user_activity: {
            ...old.user_activity,
            [UserActivityAction.evidence_chat_query]: next,
          },
        };
      });
    },
    [queryClient]
  );

  const handleOpenEvidencePowerUserModal = useCallback(() => {
    // Opening from dev-tools should also turn the feature on locally so the modal
    // is actually mounted by GlobalModalsWrapper.
    setDevForceEnabled(true);
    setEvidencePowerUserModalOpen(true);
    onRequestClose?.();
  }, [setDevForceEnabled, setEvidencePowerUserModalOpen, onRequestClose]);

  const handleResetEvidenceTrialQueryCount = useCallback(() => {
    patchEvidenceChatQueryCount(0);
  }, [patchEvidenceChatQueryCount]);

  const handleIncrementEvidenceTrialQueryCount = useCallback(() => {
    patchEvidenceChatQueryCount((prev) => prev + 1);
  }, [patchEvidenceChatQueryCount]);

  const handleForceShowBanner = useCallback(() => {
    setDevForceEnabled(true);
    setDevForceShowBanner(true);
    setBannerDismissed(false);
    patchEvidenceChatQueryCount((prev) =>
      Math.max(prev, EVIDENCE_POWER_USER_BANNER_COUNT_THRESHOLD)
    );
    onRequestClose?.();
  }, [
    setDevForceEnabled,
    setDevForceShowBanner,
    setBannerDismissed,
    patchEvidenceChatQueryCount,
    onRequestClose,
  ]);

  const handleClearForceShowBanner = useCallback(() => {
    setDevForceShowBanner(false);
  }, [setDevForceShowBanner]);

  const handleResetBannerDismissal = useCallback(() => {
    setBannerDismissed(false);
  }, [setBannerDismissed]);

  return (
    <section aria-labelledby="dev-tools-growth-heading">
      <TypographyP2SemiBold id="dev-tools-growth-heading" className="mb-2">
        Growth / upgrade modals
      </TypographyP2SemiBold>
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-0.5">
            <TypographyCaption className="text-text-secondary">
              Force-enable Evidence power-user
            </TypographyCaption>
            <TypographyCaption className="text-text-tertiary">
              Bypasses the{' '}
              <span className="font-mono">release-evidence-power-user-upgrade-modal</span>{' '}
              PostHog flag locally. Ignored in production. Current state:{' '}
              <span className="font-mono">
                {effectivelyEnabled ? 'enabled' : 'disabled'}
              </span>{' '}
              {flagEnabled
                ? '(via PostHog flag)'
                : devForceEnabled
                  ? '(via dev override)'
                  : ''}
            </TypographyCaption>
          </div>
          <Switch
            size="sm"
            className="shrink-0"
            checked={devForceEnabled}
            onCheckedChange={setDevForceEnabled}
            aria-label="Toggle Evidence power-user dev force-enable"
            data-testid="dev-tools-toggle-evidence-power-user-dev-force-enabled"
          />
        </div>

        <div className="min-w-0 space-y-0.5 border-t border-border pt-3">
          <TypographyCaption className="text-text-secondary">
            Evidence power-user upgrade modal
          </TypographyCaption>
          <TypographyCaption className="text-text-tertiary">
            Contextual upgrade modal for Clinician-trial users who have been power-using
            Evidence. The personalised count comes from{' '}
            <span className="font-mono">user_activity.evidence_chat_query</span> on the
            user payload; use +1 / Reset to patch the cached user in React Query for local
            previews.
          </TypographyCaption>
          <TypographyCaption className="text-text-secondary">
            Evidence chat query count (cached user):{' '}
            <span className="font-mono tabular-nums">{evidenceChatQueryCount}</span>
          </TypographyCaption>
        </div>
        <Button
          type="button"
          variant="pro"
          size="sm"
          onClick={handleOpenEvidencePowerUserModal}
          data-testid="dev-tools-open-evidence-power-user-modal"
        >
          Open Evidence power-user modal
        </Button>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={handleIncrementEvidenceTrialQueryCount}
            data-testid="dev-tools-increment-evidence-trial-query-count"
          >
            +1 to count
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={handleResetEvidenceTrialQueryCount}
            data-testid="dev-tools-reset-evidence-trial-query-count"
          >
            Reset to 0
          </Button>
        </div>

        <div className="mt-1 space-y-0.5 border-t border-border pt-3">
          <TypographyCaption className="text-text-secondary">
            Evidence chat banner
          </TypographyCaption>
          <TypographyCaption className="text-text-tertiary">
            Shows automatically after {EVIDENCE_POWER_USER_BANNER_COUNT_THRESHOLD}{' '}
            Evidence submissions.
          </TypographyCaption>
          <TypographyCaption className="text-text-secondary">
            Dismissed:{' '}
            <span className="font-mono">{bannerDismissed ? 'true' : 'false'}</span>
          </TypographyCaption>
          <TypographyCaption className="text-text-secondary">
            Dev force-show banner:{' '}
            <span className="font-mono">{devForceShowBanner ? 'true' : 'false'}</span>
          </TypographyCaption>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={handleForceShowBanner}
            data-testid="dev-tools-force-show-evidence-power-user-banner"
          >
            Force show
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={handleClearForceShowBanner}
            data-testid="dev-tools-clear-force-show-evidence-power-user-banner"
            disabled={!devForceShowBanner}
          >
            Clear force-show
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleResetBannerDismissal}
          data-testid="dev-tools-reset-evidence-power-user-banner-dismissal"
          disabled={!bannerDismissed}
        >
          Reset dismissal
        </Button>
      </div>
    </section>
  );
};
